import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle, useRef } from "react";
import { useLanguage } from "../../context/LanguageContext";
import { X } from "lucide-react";
import api from "../../lib/api.ts";
import { useCurrencyUtils } from "../../utils/currencyUtils";
const LeftSidebar = forwardRef(
  ({ onFiltersChange = () => { }, onClose, isMobile, vehicles = [] }, ref) => {
    const { language, translate } = useLanguage();
    const isRTL = language === "ar";
    const [bodyTypes, setBodyTypes] = useState([]);
    const [selectedBodyTypes, setSelectedBodyTypes] = useState([]);
    const handleBodyTypeChange = (id) => {
      const newSelected = selectedBodyTypes.includes(id)
        ? selectedBodyTypes.filter((typeId) => typeId !== id)
        : [...selectedBodyTypes, id];
      setSelectedBodyTypes(newSelected);
      // Notify parent component of filter changes
      updateParentFilters(newSelected, "bodyTypes");
    };
    const [drivelineTypes, setDrivelineTypes] = useState([]);
    const [selectedDrivelineTypes, setSelectedDrivelineTypes] = useState([]);
    const [transmissionTypes, setTransmissionTypes] = useState([]);
    const [selectedTransmissionTypes, setSelectedTransmissionTypes] = useState(
      [],
    );
    const [fuelTypes, setFuelTypes] = useState([]);
    const [selectedFuelTypes, setSelectedFuelTypes] = useState([]);
    const [featuredFilter, setFeaturedFilter] = useState(""); // "yes", "no", or ""
    // New filter states
    const [cylinders, setCylinders] = useState([]);
    const [selectedCylinders, setSelectedCylinders] = useState([]);
    const [engines, setEngines] = useState([]);
    const [selectedEngines, setSelectedEngines] = useState([]);
    const [highlights, setHighlights] = useState([]);
    const [selectedHighlights, setSelectedHighlights] = useState([]);
    const [specifications, setSpecifications] = useState([]);
    const [selectedSpecifications, setSelectedSpecifications] = useState([]);
    const [exteriorColors, setExteriorColors] = useState([]);
    const [selectedExteriorColors, setSelectedExteriorColors] = useState([]);
    const [interiorColors, setInteriorColors] = useState([]);
    const [selectedInteriorColors, setSelectedInteriorColors] = useState([]);
    const [primaryDamageTypes, setPrimaryDamageTypes] = useState([]);
    const [selectedPrimaryDamageTypes, setSelectedPrimaryDamageTypes] =
      useState([]);
    const [minPrice, setMinPrice] = useState(0);
    const [maxPrice, setMaxPrice] = useState(1000000);
    const [absoluteMin, setAbsoluteMin] = useState(0);
    const [absoluteMax, setAbsoluteMax] = useState(1000000);
    const boundsInitialized = useRef(false);

    const { displayPrice } = useCurrencyUtils();

    // Update absolute min/max based on vehicles
    useEffect(() => {
      if (vehicles && vehicles.length > 0) {
        const prices = vehicles.map(v =>
          Number(v.buyNowPrice || v.latestBid || v.currentBid || v.startingBid || v.price || v.sellingPrice || 0)
        ).filter(p => p > 0);

        if (prices.length > 0) {
          const vMin = Math.min(...prices);
          const vMax = Math.max(...prices);

          if (!boundsInitialized.current) {
            setAbsoluteMin(vMin);
            const fixedMax = vMax > vMin ? vMax : vMin + 1000;
            setAbsoluteMax(fixedMax);
            setMinPrice(vMin);
            setMaxPrice(fixedMax);
            boundsInitialized.current = true;
          } else {
            setAbsoluteMin(prevAbsMin => {
              if (vMin < prevAbsMin) {
                setMinPrice(prevMin => prevMin === prevAbsMin ? vMin : prevMin);
                return vMin;
              }
              return prevAbsMin;
            });
            
            setAbsoluteMax(prevAbsMax => {
              if (vMax > prevAbsMax) {
                setMaxPrice(prevMax => prevMax === prevAbsMax ? vMax : prevMax);
                return vMax;
              }
              return prevAbsMax;
            });
          }
        }
      }
    }, [vehicles]);

    // Ensure selection is always valid and within bounds
    // useEffect(() => {
    //   if (minPrice > maxPrice && maxPrice > absoluteMin) {
    //     setMinPrice(maxPrice - 100);
    //   }
    //   if (minPrice < absoluteMin) setMinPrice(absoluteMin);
    //   if (maxPrice > absoluteMax) setMaxPrice(absoluteMax);
    // }, [absoluteMin, absoluteMax, minPrice, maxPrice]);


    const [makes, setMakes] = useState([]);
    const [selectedMakes, setSelectedMakes] = useState([]);
    const [showAllMakes, setShowAllMakes] = useState(false);
    const [models, setModels] = useState([]);
    const [selectedModels, setSelectedModels] = useState([]);
    const [showAllModels, setShowAllModels] = useState(false);
    const [variants, setVariants] = useState([]);
    const [selectedVariants, setSelectedVariants] = useState([]);
    const [showAllVariants, setShowAllVariants] = useState(false);
    const [selectedYears, setSelectedYears] = useState([]);
    const [showAllYears, setShowAllYears] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
      featured: true,
      make: true,
      model: true,
      variant: true,
      year: true,
      bodyType: false,
      driveline: false,
      transmission: false,
      fuel: false,
      cylinders: false,
      engines: false,
      highlights: false,
      exteriorColors: false,
      interiorColors: false,
      damageTypes: false,
      specifications: false,
    });
    const toggleSection = (section) => {
      setExpandedSections((prev) => ({
        ...prev,
        [section]: !prev[section],
      }));
    };
    useEffect(() => {
      fetch(`${process.env.REACT_APP_API_URL}/bodytypes`)
        .then((res) => res.json())
        .then((data) => setBodyTypes(Array.isArray(data) ? data : []))
        .catch((err) => {
          console.error("Error fetching bodytypes:", err);
          setBodyTypes([]);
        });
      fetch(`${process.env.REACT_APP_API_URL}/drivelinetypes`)
        .then((res) => res.json())
        .then((data) => setDrivelineTypes(Array.isArray(data) ? data : []))
        .catch((err) => {
          console.error("Error fetching drivelinetypes:", err);
          setDrivelineTypes([]);
        });
      fetch(`${process.env.REACT_APP_API_URL}/transmissiontypes`)
        .then((res) => res.json())
        .then((data) => setTransmissionTypes(Array.isArray(data) ? data : []))
        .catch((err) => {
          console.error("Error fetching transmissiontypes:", err);
          setTransmissionTypes([]);
        });
      fetch(`${process.env.REACT_APP_API_URL}/fueltypes`)
        .then((res) => res.json())
        .then((data) => setFuelTypes(Array.isArray(data) ? data : []))
        .catch((err) => {
          console.error("Error fetching fueltypes:", err);
          setFuelTypes([]);
        });
      // Fetch new filter data
      fetch(`${process.env.REACT_APP_API_URL}/cylinders`)
        .then((res) => res.json())
        .then((data) => setCylinders(Array.isArray(data) ? data : []))
        .catch((err) => {
          console.error("Error fetching cylinders:", err);
          setCylinders([]);
        });
      fetch(`${process.env.REACT_APP_API_URL}/engines`)
        .then((res) => res.json())
        .then((data) => setEngines(Array.isArray(data) ? data : []))
        .catch((err) => {
          console.error("Error fetching engines:", err);
          setEngines([]);
        });
      fetch(`${process.env.REACT_APP_API_URL}/highlights`)
        .then((res) => res.json())
        .then((data) => setHighlights(Array.isArray(data) ? data : []))
        .catch((err) => {
          console.error("Error fetching highlights:", err);
          setHighlights([]);
        });
      fetch(`${process.env.REACT_APP_API_URL}/specifications`)
        .then((res) => res.json())
        .then((data) => setSpecifications(Array.isArray(data) ? data : []))
        .catch((err) => {
          console.error("Error fetching specifications:", err);
          setSpecifications([]);
        });
      fetch(`${process.env.REACT_APP_API_URL}/exteriorcolors`)
        .then((res) => res.json())
        .then((data) => setExteriorColors(Array.isArray(data) ? data : []))
        .catch((err) => {
          console.error("Error fetching exterior colors:", err);
          setExteriorColors([]);
        });
      fetch(`${process.env.REACT_APP_API_URL}/interiorcolors`)
        .then((res) => res.json())
        .then((data) => setInteriorColors(Array.isArray(data) ? data : []))
        .catch((err) => {
          console.error("Error fetching interior colors:", err);
          setInteriorColors([]);
        });
      fetch(`${process.env.REACT_APP_API_URL}/damagetypes`)
        .then((res) => res.json())
        .then((data) => setPrimaryDamageTypes(Array.isArray(data) ? data : []))
        .catch((err) => {
          console.error("Error fetching primary damage types:", err);
          setPrimaryDamageTypes([]);
        });
      // Fetch Makes
      api
        .get("/makes")
        .then((res) => setMakes(Array.isArray(res.data) ? res.data : []))
        .catch((err) => console.error("Error fetching makes:", err));
    }, []);
    // Fetch Models when selectedMakes changes
    useEffect(() => {
      if (selectedMakes.length === 0) {
        setModels([]);
        return;
      }
      // Try to fetch for all selected makes. Assuming backend supports comma-separated ids if they requested multi-select.
      // Otherwise, we'd need multiple API calls or a "getAllModels" endpoint.
      api
        .get(`/models?makeId=${selectedMakes.join(",")}`)
        .then((res) => setModels(Array.isArray(res.data) ? res.data : []))
        .catch((err) => console.error("Error fetching models:", err));
    }, [selectedMakes]);
    // Fetch Variants when selectedModels changes
    useEffect(() => {
      if (selectedModels.length === 0) {
        setVariants([]);
        return;
      }
      api
        .get(`/variants?modelId=${selectedModels.join(",")}`)
        .then((res) => setVariants(Array.isArray(res.data) ? res.data : []))
        .catch((err) => console.error("Error fetching variants:", err));
    }, [selectedModels]);
    const handleMakeChange = (id) => {
      const newSelected = selectedMakes.includes(id)
        ? selectedMakes.filter((makeId) => makeId !== id)
        : [...selectedMakes, id];
      setSelectedMakes(newSelected);
      // If no makes selected, clear children
      if (newSelected.length === 0) {
        setSelectedModels([]);
        setSelectedVariants([]);
        setSelectedYears([]);
        updateParentFilters([], "model");
        updateParentFilters([], "variant");
        updateParentFilters([], "year");
      }
      updateParentFilters(newSelected, "make");
    };
    const handleModelChange = (id) => {
      const newSelected = selectedModels.includes(id)
        ? selectedModels.filter((modelId) => modelId !== id)
        : [...selectedModels, id];
      setSelectedModels(newSelected);
      if (newSelected.length === 0) {
        setSelectedVariants([]);
        setSelectedYears([]);
        updateParentFilters([], "variant");
        updateParentFilters([], "year");
      }
      updateParentFilters(newSelected, "model");
    };
    const handleVariantChange = (id) => {
      const newSelected = selectedVariants.includes(id)
        ? selectedVariants.filter((variantId) => variantId !== id)
        : [...selectedVariants, id];
      setSelectedVariants(newSelected);
      updateParentFilters(newSelected, "variant");
    };
    const handleYearChange = (year) => {
      const newSelected = selectedYears.includes(year)
        ? selectedYears.filter((y) => y !== year)
        : [...selectedYears, year];
      setSelectedYears(newSelected);
      updateParentFilters(newSelected, "year");
    };
    const handleDrivelineTypeChange = (id) => {
      const newSelected = selectedDrivelineTypes.includes(id)
        ? selectedDrivelineTypes.filter((typeId) => typeId !== id)
        : [...selectedDrivelineTypes, id];
      setSelectedDrivelineTypes(newSelected);
      // Notify parent component of filter changes
      updateParentFilters(newSelected, "drivelineTypes");
    };
    const handleTransmissionTypeChange = (id) => {
      const newSelected = selectedTransmissionTypes.includes(id)
        ? selectedTransmissionTypes.filter((typeId) => typeId !== id)
        : [...selectedTransmissionTypes, id];
      setSelectedTransmissionTypes(newSelected);
      // Notify parent component of filter changes
      updateParentFilters(newSelected, "transmissionTypes");
    };
    const handleFuelTypeChange = (id) => {
      const newSelected = selectedFuelTypes.includes(id)
        ? selectedFuelTypes.filter((typeId) => typeId !== id)
        : [...selectedFuelTypes, id];
      setSelectedFuelTypes(newSelected);
      // Notify parent component of filter changes
      updateParentFilters(newSelected, "fuelTypes");
    };
    const handleFeaturedChange = (value) => {
      setFeaturedFilter(value);
      // Notify parent component of filter changes
      updateParentFilters(value, "featured");
    };
    // New filter handlers
    const handleCylinderChange = (id) => {
      const newSelected = selectedCylinders.includes(id)
        ? selectedCylinders.filter((typeId) => typeId !== id)
        : [...selectedCylinders, id];
      setSelectedCylinders(newSelected);
      updateParentFilters(newSelected, "cylinders");
    };
    const handleEngineChange = (id) => {
      const newSelected = selectedEngines.includes(id)
        ? selectedEngines.filter((typeId) => typeId !== id)
        : [...selectedEngines, id];
      setSelectedEngines(newSelected);
      updateParentFilters(newSelected, "engines");
    };
    const handleHighlightChange = (id) => {
      const newSelected = selectedHighlights.includes(id)
        ? selectedHighlights.filter((typeId) => typeId !== id)
        : [...selectedHighlights, id];
      setSelectedHighlights(newSelected);
      updateParentFilters(newSelected, "highlights");
    };
    const handleSpecificationChange = (id) => {
      const newSelected = selectedSpecifications.includes(id)
        ? selectedSpecifications.filter((typeId) => typeId !== id)
        : [...selectedSpecifications, id];
      setSelectedSpecifications(newSelected);
      updateParentFilters(newSelected, "specifications");
    };
    const handleExteriorColorChange = (id) => {
      const newSelected = selectedExteriorColors.includes(id)
        ? selectedExteriorColors.filter((typeId) => typeId !== id)
        : [...selectedExteriorColors, id];
      setSelectedExteriorColors(newSelected);
      updateParentFilters(newSelected, "exteriorColors");
    };
    const handleInteriorColorChange = (id) => {
      const newSelected = selectedInteriorColors.includes(id)
        ? selectedInteriorColors.filter((typeId) => typeId !== id)
        : [...selectedInteriorColors, id];
      setSelectedInteriorColors(newSelected);
      updateParentFilters(newSelected, "interiorColors");
    };
    const handlePrimaryDamageTypeChange = (id) => {
      const newSelected = selectedPrimaryDamageTypes.includes(id)
        ? selectedPrimaryDamageTypes.filter((typeId) => typeId !== id)
        : [...selectedPrimaryDamageTypes, id];
      setSelectedPrimaryDamageTypes(newSelected);
      updateParentFilters(newSelected, "primaryDamageTypes");
    };
    const handlePriceChange = (type, value) => {
      let val = parseInt(value, 10);
      if (isNaN(val)) val = 0; // Handle empty or invalid input

      if (type === "min") {
        // Ensure minPrice doesn't exceed maxPrice - 100
        val = Math.min(val, maxPrice - 100);
        // Ensure minPrice is not less than absoluteMin
        val = Math.max(val, absoluteMin);
        setMinPrice(val);
        updateParentFilters({ min: val, max: maxPrice }, "price");
      } else { // type === "max"
        // Ensure maxPrice doesn't go below minPrice + 100
        val = Math.max(val, minPrice + 100);
        // Ensure maxPrice is not greater than absoluteMax
        val = Math.min(val, absoluteMax);
        setMaxPrice(val);
        updateParentFilters({ min: minPrice, max: val }, "price");
      }
    };
    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      setFeaturedFilter: (value) => {
        const normalized = value === "yes" ? "yes" : value === "no" ? "no" : "";
        setFeaturedFilter(normalized);
        updateParentFilters(normalized, "featured");
      },
      removeFilter: (filterType, filterId) => {
        switch (filterType) {
          case "bodyTypes":
            setSelectedBodyTypes((prev) => {
              const newSelected = prev.filter((id) => id !== filterId);
              updateParentFilters(newSelected, "bodyTypes");
              return newSelected;
            });
            break;
          case "drivelineTypes":
            setSelectedDrivelineTypes((prev) => {
              const newSelected = prev.filter((id) => id !== filterId);
              updateParentFilters(newSelected, "drivelineTypes");
              return newSelected;
            });
            break;
          case "transmissionTypes":
            setSelectedTransmissionTypes((prev) => {
              const newSelected = prev.filter((id) => id !== filterId);
              updateParentFilters(newSelected, "transmissionTypes");
              return newSelected;
            });
            break;
          case "fuelTypes":
            setSelectedFuelTypes((prev) => {
              const newSelected = prev.filter((id) => id !== filterId);
              updateParentFilters(newSelected, "fuelTypes");
              return newSelected;
            });
            break;
          case "featured":
            setFeaturedFilter("");
            updateParentFilters("", "featured");
            break;
          case "cylinders":
            setSelectedCylinders((prev) => {
              const newSelected = prev.filter((id) => id !== filterId);
              updateParentFilters(newSelected, "cylinders");
              return newSelected;
            });
            break;
          case "engines":
            setSelectedEngines((prev) => {
              const newSelected = prev.filter((id) => id !== filterId);
              updateParentFilters(newSelected, "engines");
              return newSelected;
            });
            break;
          case "highlights":
            setSelectedHighlights((prev) => {
              const newSelected = prev.filter((id) => id !== filterId);
              updateParentFilters(newSelected, "highlights");
              return newSelected;
            });
            break;
          case "specifications":
            setSelectedSpecifications((prev) => {
              const newSelected = prev.filter((id) => id !== filterId);
              updateParentFilters(newSelected, "specifications");
              return newSelected;
            });
            break;
          case "exteriorColors":
            setSelectedExteriorColors((prev) => {
              const newSelected = prev.filter((id) => id !== filterId);
              updateParentFilters(newSelected, "exteriorColors");
              return newSelected;
            });
            break;
          case "interiorColors":
            setSelectedInteriorColors((prev) => {
              const newSelected = prev.filter((id) => id !== filterId);
              updateParentFilters(newSelected, "interiorColors");
              return newSelected;
            });
            break;
          case "primaryDamageTypes":
            setSelectedPrimaryDamageTypes((prev) => {
              const newSelected = prev.filter((id) => id !== filterId);
              updateParentFilters(newSelected, "primaryDamageTypes");
              return newSelected;
            });
            break;
          case "price":
            setMinPrice(absoluteMin);
            setMaxPrice(absoluteMax);
            updateParentFilters({ min: absoluteMin, max: absoluteMax }, "price");
            break;
          case "make":
            setSelectedMakes((prev) => {
              const newSelected = prev.filter((id) => id !== filterId);
              updateParentFilters(newSelected, "make");
              return newSelected;
            });
            break;
          case "model":
            setSelectedModels((prev) => {
              const newSelected = prev.filter((id) => id !== filterId);
              updateParentFilters(newSelected, "model");
              return newSelected;
            });
            break;
          case "variant":
            setSelectedVariants((prev) => {
              const newSelected = prev.filter((id) => id !== filterId);
              updateParentFilters(newSelected, "variant");
              return newSelected;
            });
            break;
          case "year":
            setSelectedYears((prev) => {
              const newSelected = prev.filter((id) => id !== filterId);
              updateParentFilters(newSelected, "year");
              return newSelected;
            });
            break;
          default:
            break;
        }
      },
      clearFilterType: (filterType) => {
        switch (filterType) {
          case "bodyTypes":
            setSelectedBodyTypes([]);
            updateParentFilters([], "bodyTypes");
            break;
          case "drivelineTypes":
            setSelectedDrivelineTypes([]);
            updateParentFilters([], "drivelineTypes");
            break;
          case "transmissionTypes":
            setSelectedTransmissionTypes([]);
            updateParentFilters([], "transmissionTypes");
            break;
          case "fuelTypes":
            setSelectedFuelTypes([]);
            updateParentFilters([], "fuelTypes");
            break;
          case "featured":
            setFeaturedFilter("");
            updateParentFilters("", "featured");
            break;
          case "make":
            setSelectedMakes([]);
            setSelectedModels([]);
            setSelectedVariants([]);
            setSelectedYears([]);
            updateParentFilters([], "make");
            updateParentFilters([], "model");
            updateParentFilters([], "variant");
            updateParentFilters([], "year");
            break;
          case "model":
            setSelectedModels([]);
            setSelectedVariants([]);
            setSelectedYears([]);
            updateParentFilters([], "model");
            updateParentFilters([], "variant");
            updateParentFilters([], "year");
            break;
          case "variant":
            setSelectedVariants([]);
            updateParentFilters([], "variant");
            break;
          case "year":
            setSelectedYears([]);
            updateParentFilters([], "year");
            break;
          case "cylinders":
            setSelectedCylinders([]);
            updateParentFilters([], "cylinders");
            break;
          case "engines":
            setSelectedEngines([]);
            updateParentFilters([], "engines");
            break;
          case "highlights":
            setSelectedHighlights([]);
            updateParentFilters([], "highlights");
            break;
          case "specifications":
            setSelectedSpecifications([]);
            updateParentFilters([], "specifications");
            break;
          case "exteriorColors":
            setSelectedExteriorColors([]);
            updateParentFilters([], "exteriorColors");
            break;
          case "interiorColors":
            setSelectedInteriorColors([]);
            updateParentFilters([], "interiorColors");
            break;
          case "primaryDamageTypes":
            setSelectedPrimaryDamageTypes([]);
            updateParentFilters([], "primaryDamageTypes");
            break;
          case "price":
            setMinPrice(absoluteMin);
            setMaxPrice(absoluteMax);
            updateParentFilters({ min: absoluteMin, max: absoluteMax }, "price");
            break;
          default:
            break;
        }
      },
      resetAllFilters: () => {
        setSelectedBodyTypes([]);
        setSelectedDrivelineTypes([]);
        setSelectedTransmissionTypes([]);
        setSelectedFuelTypes([]);
        setFeaturedFilter("");
        setSelectedCylinders([]);
        setSelectedEngines([]);
        setSelectedHighlights([]);
        setSelectedSpecifications([]);
        setSelectedExteriorColors([]);
        setSelectedInteriorColors([]);
        setSelectedPrimaryDamageTypes([]);
        setMinPrice(absoluteMin);
        setMaxPrice(absoluteMax);
        setSelectedMakes([]);
        setSelectedModels([]);
        setSelectedVariants([]);
        setSelectedYears([]);
        const emptyFilters = {
          bodyTypes: [],
          drivelineTypes: [],
          transmissionTypes: [],
          fuelTypes: [],
          featured: "",
          cylinders: [],
          engines: [],
          highlights: [],
          specifications: [],
          exteriorColors: [],
          interiorColors: [],
          primaryDamageTypes: [],
          price: { min: absoluteMin, max: absoluteMax },
          make: [],
          model: [],
          variant: [],
          year: [],
        };
        const filterData = {
          priceBounds: { min: absoluteMin, max: absoluteMax },
        };
        onFiltersChange(emptyFilters, filterData);
      },
    }));
    // Helper function to update parent with current filter state
    const updateParentFilters = (newSelected, filterType) => {
      const currentFilters = {
        bodyTypes: selectedBodyTypes,
        drivelineTypes: selectedDrivelineTypes,
        transmissionTypes: selectedTransmissionTypes,
        fuelTypes: selectedFuelTypes,
        featured: featuredFilter,
        cylinders: selectedCylinders,
        engines: selectedEngines,
        highlights: selectedHighlights,
        specifications: selectedSpecifications,
        exteriorColors: selectedExteriorColors,
        interiorColors: selectedInteriorColors,
        primaryDamageTypes: selectedPrimaryDamageTypes,
        price: { min: minPrice, max: maxPrice },
        make: selectedMakes,
        model: selectedModels,
        variant: selectedVariants,
        year: selectedYears,
        [filterType]: newSelected,
      };
      // Get the actual names for the selected IDs
      const filterData = {
        bodyTypes: bodyTypes,
        drivelineTypes: drivelineTypes,
        transmissionTypes: transmissionTypes,
        fuelTypes: fuelTypes,
        featured: [
          { id: "yes", name: "Yes" },
          { id: "no", name: "No" },
        ],
        cylinders: cylinders,
        engines: engines,
        highlights: highlights,
        specifications: specifications,
        exteriorColors: exteriorColors,
        interiorColors: interiorColors,
        primaryDamageTypes: primaryDamageTypes,
        make: makes,
        model: models,
        variant: variants, // Added variant data here
        year: Array.from({ length: 30 }, (_, i) => {
          const yr = (new Date().getFullYear() - i).toString();
          return { id: yr, name: yr };
        }),
        price: [], // No static data needed for price
        priceBounds: { min: absoluteMin, max: absoluteMax },
      };
      onFiltersChange(currentFilters, filterData);
    };
    ///---new ui---
    const FEATURED_LIMIT = 3;
    const [showAllFeatured, setShowAllFeatured] = useState(false);
    const [showAllBodyTypes, setShowAllBodyTypes] = useState(false);
    const [showAllDriveline, setShowAllDriveline] = useState(false);
    const [showAllTransmission, setShowAllTransmission] = useState(false);
    const [showAllFuel, setShowAllFuel] = useState(false);
    const [showAllHighlights, setShowAllHighlights] = useState(false);
    const [showAllSpecifications, setShowAllSpecifications] = useState(false);
    const [showAllCylinders, setShowAllCylinders] = useState(false);
    const [showAllEngines, setShowAllEngines] = useState(false);
    const [showAllExteriorColors, setShowAllExteriorColors] = useState(false);
    const [showAllInteriorColors, setShowAllInteriorColors] = useState(false);
    const [showAllPrimaryDamageTypes, setShowAllPrimaryDamageTypes] =
      useState(false);
    const featuredOptions = [
      { id: "yes", label: translate("sidebar.options.yes") },
      { id: "no", label: translate("sidebar.options.no") },
      { id: "", label: translate("sidebar.options.all") },
      // future options can be added here
    ];

    // Calculate dynamic counts based on vehicles prop
    const dynamicCounts = useMemo(() => {
      const counts = {
        bodyTypes: {},
        drivelineTypes: {},
        transmissionTypes: {},
        fuelTypes: {},
        cylinders: {},
        engines: {},
        highlights: {},
        specifications: {},
        exteriorColors: {},
        interiorColors: {},
        primaryDamageTypes: {},
        makes: {},
        models: {},
        variants: {},
        years: {},
        featured: { all: vehicles.length, featuredOnly: vehicles.filter(v => v.isFeatured).length }
      };

      vehicles.forEach((v) => {
        const categories = [
          { key: "bodyTypes", id: v.bodyTypeId },
          { key: "drivelineTypes", id: v.drivelineTypeId },
          { key: "transmissionTypes", id: v.transmissionTypeId },
          { key: "fuelTypes", id: v.fuelTypeId },
          { key: "cylinders", id: v.cylinderId },
          { key: "engines", id: v.engineId },
          { key: "exteriorColors", id: v.exteriorColorId },
          { key: "interiorColors", id: v.interiorColorId },
          { key: "primaryDamageTypes", id: v.primaryDamageTypeId },
          { key: "makes", id: v.makeId },
          { key: "models", id: v.modelId },
          { key: "variants", id: v.variantId },
          { key: "years", id: v.year },
        ];

        categories.forEach(({ key, id }) => {
          if (id !== undefined && id !== null && id !== "") {
            const stringId = String(id);
            counts[key][stringId] = (counts[key][stringId] || 0) + 1;
          }
        });

        // Count highlights (array of objects with id)
        if (Array.isArray(v.highlights)) {
          v.highlights.forEach((h) => {
            const hId = h.id || h;
            if (hId !== undefined && hId !== null && hId !== "") {
              counts.highlights[hId] = (counts.highlights[hId] || 0) + 1;
            }
          });
        }

        // Count specifications (array of objects with id)
        if (Array.isArray(v.specifications)) {
          v.specifications.forEach((s) => {
            const sId = s.id || s;
            if (sId !== undefined && sId !== null && sId !== "") {
              counts.specifications[sId] = (counts.specifications[sId] || 0) + 1;
            }
          });
        }
      });

      return counts;
    }, [vehicles]);
    const visibleFeatured = showAllFeatured
      ? featuredOptions
      : featuredOptions.slice(0, FEATURED_LIMIT);
    const visibleBodyTypes = showAllBodyTypes
      ? bodyTypes
      : bodyTypes.slice(0, 3);
    const visibleCylinders = showAllCylinders
      ? cylinders
      : cylinders.slice(0, 3);
    const visibleEngines = showAllEngines ? engines : engines.slice(0, 3);
    const visibleExteriorColors = showAllExteriorColors
      ? exteriorColors
      : exteriorColors.slice(0, 3);
    const visibleInteriorColors = showAllInteriorColors
      ? interiorColors
      : interiorColors.slice(0, 3);
    const visiblePrimaryDamageTypes = showAllPrimaryDamageTypes
      ? primaryDamageTypes
      : primaryDamageTypes.slice(0, 3);
    const visibleMakes = showAllMakes ? makes : makes.slice(0, 3);
    const visibleModels = showAllModels ? models : models.slice(0, 3);
    const visibleVariants = showAllVariants ? variants : variants.slice(0, 3);
    const years = Array.from({ length: 30 }, (_, i) =>
      (new Date().getFullYear() - i).toString(),
    );
    const visibleYears = showAllYears ? years : years.slice(0, 3);
    /* Update the return JSX to use expandable sections */
    return (
      <aside
        className={`p-4 ${isMobile ? "flex flex-col min-h-max" : ""}`}
        dir={isRTL ? "rtl" : "ltr"}
      >
        {/* Mobile Header with Close Button */}
        {isMobile && (
          <div className="flex justify-between items-center mb-6 pl-2">
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        )}
        {/* Price Range Filter - Always Visible */}
        <div className="flex items-center gap-2 mb-3 px-1">
          {/* Filter Icon - Primary Color */}
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clip-path="url(#clip0_3_2736)">
              <path
                d="M6.67 12H5.33V8H6.67V9.33H12V10.67H6.67V12ZM4 10.67H0V9.33H4V10.67ZM4 8H2.67V6.67H0V5.33H2.67V4H4V8ZM12 6.67H5.33V5.33H12V6.67ZM9.33 4H8V0H9.33V1.33H12V2.67H9.33V4ZM6.67 2.67H0V1.33H6.67V2.67Z"
                fill="#135EA0"
              />
            </g>
            <defs>
              <clipPath id="clip0_3_2736">
                <rect width="12" height="12" fill="white" />
              </clipPath>
            </defs>
          </svg>
          {/* Filters Text - White */}
          <h2 className="text-white text-sm font-bold uppercase tracking-wider px-2">
            <span>{translate("common.filters")}</span>
          </h2>
        </div>
        {/* Featured Filter */}
        <div
          className="flex justify-between items-center cursor-pointer px-2 mb-2"
          onClick={() => toggleSection("featured")}
        >
          <span className="text-title text-[12px] font-semibold">
            {translate("sidebar.filters.featured")}
          </span>
          <span className="text-lg font-bold text-description">
            {expandedSections.featured ? "−" : "+"}
          </span>
        </div>
        {expandedSections.featured && (
          <div className="mb-4 p-4 border border-default rounded-xl bg-card ">
            <div className="flex flex-wrap gap-3">
              {visibleFeatured.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleFeaturedChange(item.id)}
                  className={`px-4 py-2 rounded-full border text-sm transition cursor-pointer
                    ${featuredFilter === item.id
                      ? "border-primary/20 bg-page text-description"
                      : "border-default text-description hover:border-primary/20"
                    }`}
                >
                  {item.label}
                  {featuredFilter === item.id && (
                    <span className="text-sm text-description ml-2">
                      {item.id === "yes" ? dynamicCounts.featured.featuredOnly :
                        item.id === "no" ? (dynamicCounts.featured.all - dynamicCounts.featured.featuredOnly) :
                          dynamicCounts.featured.all}
                    </span>
                  )}
                </button>
              ))}
              {/* More button */}
              {featuredOptions.length > FEATURED_LIMIT && !showAllFeatured && (
                <button
                  type="button"
                  onClick={() => setShowAllFeatured(true)}
                  className="px-4 py-2 rounded-full border border-dashed border-default text-sm text-gray-300 hover:text-white hover:border-gray-400 transition"
                >
                  + {translate("common.more") || "More"}
                </button>
              )}
            </div>
          </div>
        )}
        {/* Highlights Filter */}
        <div
          className="flex justify-between items-center cursor-pointer px-2 mb-2 mt-4"
          onClick={() => toggleSection("highlights")}
        >
          <span className="text-title text-[12px] font-semibold">
            {translate("sidebar.filters.highlights")}
          </span>
          <span className="text-lg font-bold text-description">
            {expandedSections.highlights ? "−" : "+"}
          </span>
        </div>
        {expandedSections.highlights && (
          <div className="mb-4 p-4 border border-default rounded-xl bg-card w-full">
            <div className="flex flex-wrap gap-2 w-full">
              {(showAllHighlights
                ? highlights
                : highlights.slice(0, FEATURED_LIMIT)
              ).map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => handleHighlightChange(type.id)}
                  className={`flex justify-between items-center px-3 py-1.5 rounded-full border text-sm transition cursor-pointer
                flex-auto w-auto
                ${selectedHighlights.includes(type.id)
                      ? "border-primary-dark bg-page text-description"
                      : "border-default text-description hover:border-primary-soft"
                    }`}
                >
                  <span title={type.name}>
                    {type.name}
                  </span>
                  {selectedHighlights.includes(type.id) && (
                    <span className="text-description/70 text-xs ml-2">
                      {dynamicCounts.highlights[type.id] || 0}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {highlights.length > FEATURED_LIMIT && (
              <div className="flex justify-center py-2 border-t border-default mt-2 -mx-4 -mb-4">
                <button
                  type="button"
                  className="text-primary text-sm font-semibold hover:text-primary-dark uppercase tracking-wider"
                  onClick={() => setShowAllHighlights((prev) => !prev)}
                >
                  {showAllHighlights
                    ? translate("sidebar.buttons.seeLess")
                    : translate("sidebar.buttons.seeMore")}
                </button>
              </div>
            )}
          </div>
        )}
 {/* Year Filter - Only show if Model is selected */}
        {selectedModels.length > 0 && (
          <>
            <div
              className="flex justify-between items-center cursor-pointer px-2 mb-2"
              onClick={() => toggleSection("year")}
            >
              <span className="text-title text-[12px] font-semibold">
                {translate("search.year")}
              </span>
              <span className="text-lg font-bold text-description">
                {expandedSections.year ? "−" : "+"}
              </span>
            </div>
            {expandedSections.year && (
              <div className="bg-card border mb-4 border-default rounded-xl text-description w-full overflow-hidden">
                <ul>
                  {visibleYears.map((year) => (
                    <li
                      key={year}
                      className="flex items-center justify-between border-b border-default"
                    >
                      <label className="flex items-center cursor-pointer w-full px-4 py-2">
                        <input
                          type="checkbox"
                          checked={selectedYears.includes(year)}
                          onChange={() => handleYearChange(year)}
                          className="mr-2 custom-checkbox"
                        />
                        <span className="flex-1">{year}</span>
                        {selectedYears.includes(year) && (
                          <span className="text-sm text-description">
                            {dynamicCounts.years[year] || 0}
                          </span>
                        )}
                      </label>
                    </li>
                  ))}
                </ul>
                {years.length > 3 && (
                  <div className="flex justify-center py-2">
                    <button
                      type="button"
                      className="text-primary text-sm font-semibold hover:text-primary-dark uppercase tracking-wider"
                      onClick={() => setShowAllYears((prev) => !prev)}
                    >
                      {showAllYears
                        ? translate("sidebar.buttons.seeLess")
                        : translate("sidebar.buttons.seeMore")}
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}


        {/* Makes Filter */}
        <div
          className="flex justify-between items-center cursor-pointer px-2 mb-2"
          onClick={() => toggleSection("make")}
        >
          <span className="text-title text-[12px] font-semibold">
            {translate("search.make")}
          </span>
          <span className="text-lg font-bold text-description">
            {expandedSections.make ? "−" : "+"}
          </span>
        </div>
        {expandedSections.make && (
          <div className="bg-card border mb-4 border-default rounded-xl text-description w-full overflow-hidden">
            <ul>
              {visibleMakes.map((make) => (
                <li
                  key={make.id}
                  className="flex items-center justify-between border-b border-default"
                >
                  <label className="flex items-center cursor-pointer w-full px-4 py-2">
                    <input
                      type="checkbox"
                      checked={selectedMakes.includes(make.id)}
                      onChange={() => handleMakeChange(make.id)}
                      className="mr-2 custom-checkbox"
                    />
                    <span className="flex-1">{make.name}</span>
                    {selectedMakes.includes(make.id) && (
                      <span className="text-sm text-description">
                        {dynamicCounts.makes[make.id] || 0}
                      </span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
            {makes.length > 3 && (
              <div className="flex justify-center py-2">
                <button
                  type="button"
                  className="text-primary text-sm font-semibold hover:text-primary-dark uppercase tracking-wider"
                  onClick={() => setShowAllMakes((prev) => !prev)}
                >
                  {showAllMakes
                    ? translate("sidebar.buttons.seeLess")
                    : translate("sidebar.buttons.seeMore")}
                </button>
              </div>
            )}
          </div>
        )}
        {/* Model Filter - Only show if Make is selected */}
        {selectedMakes.length > 0 && (
          <>
            <div
              className="flex justify-between items-center cursor-pointer px-2 mb-2"
              onClick={() => toggleSection("model")}
            >
              <span className="text-title text-[12px] font-semibold">
                {translate("search.model")}
              </span>
              <span className="text-lg font-bold text-description">
                {expandedSections.model ? "−" : "+"}
              </span>
            </div>
            {expandedSections.model && (
              <div className="bg-card border mb-4 border-default rounded-xl text-description w-full overflow-hidden">
                <ul>
                  {visibleModels.map((model) => (
                    <li
                      key={model.id}
                      className="flex items-center justify-between border-b border-default"
                    >
                      <label className="flex items-center cursor-pointer w-full px-4 py-2">
                        <input
                          type="checkbox"
                          checked={selectedModels.includes(model.id)}
                          onChange={() => handleModelChange(model.id)}
                          className="mr-2 custom-checkbox"
                        />
                        <span className="flex-1">{model.name}</span>
                        {selectedModels.includes(model.id) && (
                          <span className="text-sm text-description">
                            {dynamicCounts.models[model.id] || 0}
                          </span>
                        )}
                      </label>
                    </li>
                  ))}
                </ul>
                {models.length > 3 && (
                  <div className="flex justify-center py-2">
                    <button
                      type="button"
                      className="text-primary text-sm font-semibold hover:text-primary-dark uppercase tracking-wider"
                      onClick={() => setShowAllModels((prev) => !prev)}
                    >
                      {showAllModels
                        ? translate("sidebar.buttons.seeLess")
                        : translate("sidebar.buttons.seeMore")}
                    </button>
                  </div>
                )}
                {models.length === 0 && (
                  <div className="px-4 py-2 text-[12px] text-description opacity-60">
                    {translate("search.noModels")}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {/* Variant Filter - Only show if Model is selected and variants exist */}
        {selectedModels.length > 0 && variants.length > 0 && (
          <>
            <div
              className="flex justify-between items-center cursor-pointer px-2 mb-2"
              onClick={() => toggleSection("variant")}
            >
              <span className="text-title text-[12px] font-semibold">
                {translate("addVehicle.labels.variant")}
              </span>
              <span className="text-lg font-bold text-description">
                {expandedSections.variant ? "−" : "+"}
              </span>
            </div>
            {expandedSections.variant && (
              <div className="bg-card border mb-4 border-default rounded-xl text-description w-full overflow-hidden">
                <ul>
                  {visibleVariants.map((variant) => (
                    <li
                      key={variant.id}
                      className="flex items-center justify-between border-b border-default"
                    >
                      <label className="flex items-center cursor-pointer w-full px-4 py-2">
                        <input
                          type="checkbox"
                          checked={selectedVariants.includes(variant.id)}
                          onChange={() => handleVariantChange(variant.id)}
                          className="mr-2 custom-checkbox"
                        />
                        <span className="flex-1">{variant.name}</span>
                        {selectedVariants.includes(variant.id) && (
                          <span className="text-sm text-description">
                            {dynamicCounts.variants[variant.id] || 0}
                          </span>
                        )}
                      </label>
                    </li>
                  ))}
                </ul>
                {variants.length > 3 && (
                  <div className="flex justify-center py-2">
                    <button
                      type="button"
                      className="text-primary text-sm font-semibold hover:text-primary-dark uppercase tracking-wider"
                      onClick={() => setShowAllVariants((prev) => !prev)}
                    >
                      {showAllVariants
                        ? translate("sidebar.buttons.seeLess")
                        : translate("sidebar.buttons.seeMore")}
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

       
       


        {/* Price Range Filter */}
        <div className="mb-4 p-3 border border-default rounded-xl bg-card">
          <div className="text-title text-[12px] font-semibold mb-2 flex justify-between items-center">
            <span>{translate("sidebar.filters.price")}</span>
            <span className="text-primary text-[11px] font-bold">
              {displayPrice(minPrice)} - {displayPrice(maxPrice)}
            </span>
          </div>
          <div className="flex flex-col gap-3">
            {/* Slider Container - Height matches the 14px circle */}
            <div className="relative w-full h-[14px] flex items-center">
              {/* Background Track (Dark Gray) */}
              <div className="absolute w-full h-[4px] bg-gray-700 rounded-full"></div>
              {/* Progress Bar (Blue) */}
              <div
                className="absolute h-[4px] bg-primary rounded-full z-10"
                style={{
                  left: `${((minPrice - absoluteMin) / (absoluteMax - absoluteMin)) * 100}%`,
                  width: `${((maxPrice - minPrice) / (absoluteMax - absoluteMin)) * 100}%`,
                }}
              ></div>
              {/* Range Inputs */}
              <input
                type="range"
                min={absoluteMin}
                max={absoluteMax}
                step="100"
                value={minPrice}
                onChange={(e) => {
                  const val = Math.min(
                    parseInt(e.target.value),
                    maxPrice - 100,
                  );
                  handlePriceChange("min", val);
                }}
                className={`absolute w-full appearance-none bg-transparent pointer-events-none ${minPrice > (absoluteMax - absoluteMin) / 2 ? "z-30" : "z-20"}
          [&::-webkit-slider-thumb]:appearance-none
          [&::-webkit-slider-thumb]:pointer-events-auto
          [&::-webkit-slider-thumb]:w-[14px]
          [&::-webkit-slider-thumb]:h-[14px]
          [&::-webkit-slider-thumb]:bg-white
          [&::-webkit-slider-thumb]:border-[2px]
          [&::-webkit-slider-thumb]:border-primary
          [&::-webkit-slider-thumb]:rounded-full
          [&::-webkit-slider-thumb]:cursor-pointer
          [&::-webkit-slider-thumb]:shadow-md`}
              />
              <input
                type="range"
                min={absoluteMin}
                max={absoluteMax}
                step="100"
                value={maxPrice}
                onChange={(e) => {
                  const val = Math.max(
                    parseInt(e.target.value),
                    minPrice + 100,
                  );
                  handlePriceChange("max", val);
                }}
                className={`absolute w-full appearance-none bg-transparent pointer-events-none ${maxPrice < (absoluteMax - absoluteMin) / 2 ? "z-30" : "z-20"}
          [&::-webkit-slider-thumb]:appearance-none
          [&::-webkit-slider-thumb]:pointer-events-auto
          [&::-webkit-slider-thumb]:w-[14px]
          [&::-webkit-slider-thumb]:h-[14px]
          [&::-webkit-slider-thumb]:bg-white
          [&::-webkit-slider-thumb]:border-[2px]
          [&::-webkit-slider-thumb]:border-primary
          [&::-webkit-slider-thumb]:rounded-full
          [&::-webkit-slider-thumb]:cursor-pointer
          [&::-webkit-slider-thumb]:shadow-md`}
              />
            </div>

            {/* Manual Entry Inputs */}
            <div className="flex items-center gap-2 mt-2">
              <div className="flex-1">
                <input
                  type="number"
                  value={minPrice}
                  min={absoluteMin}
                  max={maxPrice - 100}
                  onChange={(e) => setMinPrice(e.target.value)}
                  onBlur={(e) => handlePriceChange("min", e.target.value)}
                  className="w-full bg-page border border-default rounded-lg px-2 py-1 text-[11px] text-title focus:outline-none focus:border-primary"
                  placeholder="Min"
                />
              </div>
              <span className="text-gray-400 text-xs">-</span>
              <div className="flex-1">
                <input
                  type="number"
                  value={maxPrice}
                  min={minPrice + 100}
                  max={absoluteMax}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  onBlur={(e) => handlePriceChange("max", e.target.value)}
                  className="w-full bg-page border border-default rounded-lg px-2 py-1 text-[11px] text-title focus:outline-none focus:border-primary"
                  placeholder="Max"
                />
              </div>
            </div>

            <div className="flex justify-between text-[10px] text-gray-500 px-1 mt-1 font-medium italic">
              <span>{displayPrice(absoluteMin)}</span>
              <span>{displayPrice(absoluteMax)}</span>
            </div>
          </div>
        </div>
        {/* Body Type */}
        <div
          className="flex justify-between items-center cursor-pointer px-2 mb-2"
          onClick={() => toggleSection("bodyType")}
        >
          <span className="text-title text-[12px] font-semibold">
            {translate("sidebar.filters.bodyType")}
          </span>
          <span className="text-lg font-bold text-description">
            {expandedSections.bodyType ? "−" : "+"}
          </span>
        </div>
        {expandedSections.bodyType && (
          <div className="bg-card border mb-4 border-default rounded-xl text-description w-full overflow-hidden">
            <ul>
              {visibleBodyTypes.map((type) => (
                <li
                  key={type.id}
                  className="flex items-center justify-between border-b border-default"
                >
                  <label className="flex items-center cursor-pointer w-full px-4 py-2">
                    <input
                      type="checkbox"
                      checked={selectedBodyTypes.includes(type.id)}
                      onChange={() => handleBodyTypeChange(type.id)}
                      className="mr-2 custom-checkbox"
                    />
                    <span className="flex-1">{type.name}</span>
                    {selectedBodyTypes.includes(type.id) && (
                      <span className="text-sm text-description">
                        {dynamicCounts.bodyTypes[type.id] || 0}
                      </span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
            {bodyTypes.length > 3 && (
              <div className="flex justify-center py-2">
                <button
                  type="button"
                  className="text-primary text-sm font-semibold hover:text-primary-dark uppercase tracking-wider"
                  onClick={() => setShowAllBodyTypes((prev) => !prev)}
                >
                  {showAllBodyTypes
                    ? translate("sidebar.buttons.seeLess")
                    : translate("sidebar.buttons.seeMore")}
                </button>
              </div>
            )}
          </div>
        )}
        {/* Driveline Filter */}
        <div
          className="flex justify-between items-center cursor-pointer px-2 mb-2"
          onClick={() => toggleSection("driveline")}
        >
          <span className="text-title text-[12px] font-semibold">
            {translate("sidebar.filters.driveline")}
          </span>
          <span className="text-lg font-bold text-description">
            {expandedSections.driveline ? "−" : "+"}
          </span>
        </div>
        {expandedSections.driveline && (
          <div className="mb-4 p-4 border border-default rounded-xl bg-card w-full">
            <div className="flex flex-wrap gap-2 w-full">
              {(showAllDriveline
                ? drivelineTypes
                : drivelineTypes.slice(0, FEATURED_LIMIT)
              ).map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => handleDrivelineTypeChange(type.id)}
                  className={`flex justify-between items-center px-3 py-1.5 rounded-full border text-sm transition cursor-pointer
            flex-auto w-auto
            ${selectedDrivelineTypes.includes(type.id)
                      ? "border-primary-dark bg-page text-description"
                      : "border-default text-description hover:border-primary-soft"
                    }`}
                >
                  <span title={type.name}>
                    {type.name}
                  </span>
                  {selectedDrivelineTypes.includes(type.id) && (
                    <span className="text-description text-xs ml-2">
                      {dynamicCounts.drivelineTypes[type.id] || 0}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {drivelineTypes.length > FEATURED_LIMIT && (
              <div className="flex justify-center py-2 border-t border-default mt-2 -mx-4 -mb-4">
                <button
                  type="button"
                  className="text-primary text-sm font-semibold hover:text-primary-dark uppercase tracking-wider"
                  onClick={() => setShowAllDriveline((prev) => !prev)}
                >
                  {showAllDriveline
                    ? translate("sidebar.buttons.seeLess")
                    : translate("sidebar.buttons.seeMore")}
                </button>
              </div>
            )}
          </div>
        )}
        {/* Transmission Filter */}
        <div
          className="flex justify-between items-center cursor-pointer px-2 mb-2 mt-4"
          onClick={() => toggleSection("transmission")}
        >
          <span className="text-title text-[12px] font-semibold">
            {translate("sidebar.filters.transmission")}
          </span>
          <span className="text-lg font-bold text-description">
            {expandedSections.transmission ? "−" : "+"}
          </span>
        </div>
        {expandedSections.transmission && (
          <div className="mb-4 p-4 border border-default rounded-xl bg-card w-full">
            <div className="flex flex-wrap gap-2 w-full">
              {(showAllTransmission
                ? transmissionTypes
                : transmissionTypes.slice(0, FEATURED_LIMIT)
              ).map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => handleTransmissionTypeChange(type.id)}
                  className={`flex justify-between items-center px-3 py-1.5 rounded-full border text-sm transition cursor-pointer
              flex-auto w-auto
              ${selectedTransmissionTypes.includes(type.id)
                      ? "border-primary-dark bg-page text-description"
                      : "border-default text-description hover:border-primary-soft"
                    }`}
                >
                  <span title={type.name}>
                    {type.name}
                  </span>
                  {selectedTransmissionTypes.includes(type.id) && (
                    <span className="text-description text-xs ml-2">
                      {dynamicCounts.transmissionTypes[type.id] || 0}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {transmissionTypes.length > FEATURED_LIMIT && (
              <div className="flex justify-center py-2 border-t border-default mt-2 -mx-4 -mb-4">
                <button
                  type="button"
                  className="text-primary text-sm font-semibold hover:text-primary-dark uppercase tracking-wider"
                  onClick={() => setShowAllTransmission((prev) => !prev)}
                >
                  {showAllTransmission
                    ? translate("sidebar.buttons.seeLess")
                    : translate("sidebar.buttons.seeMore")}
                </button>
              </div>
            )}
          </div>
        )}
        {/* Fuel Filter */}
        <div
          className="flex justify-between items-center cursor-pointer px-2 mb-2 mt-4"
          onClick={() => toggleSection("fuel")}
        >
          <span className="text-title text-[12px] font-semibold">
            {translate("sidebar.filters.fuel")}
          </span>
          <span className="text-lg font-bold text-description">
            {expandedSections.fuel ? "−" : "+"}
          </span>
        </div>
        {expandedSections.fuel && (
          <div className="mb-4 p-4 border border-default rounded-xl bg-card w-full">
            <div className="flex flex-wrap gap-2 w-full">
              {(showAllFuel
                ? fuelTypes
                : fuelTypes.slice(0, FEATURED_LIMIT)
              ).map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => handleFuelTypeChange(type.id)}
                  className={`flex justify-between items-center px-3 py-1.5 rounded-full border text-sm transition cursor-pointer
                flex-auto w-auto
                ${selectedFuelTypes.includes(type.id)
                      ? "border-primary-dark bg-page text-description"
                      : "border-default text-description hover:border-primary-soft"
                    }`}
                >
                  <span title={type.name}>
                    {type.name}
                  </span>
                  {selectedFuelTypes.includes(type.id) && (
                    <span className="text-description text-xs ml-2">
                      {dynamicCounts.fuelTypes[type.id] || 0}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {fuelTypes.length > FEATURED_LIMIT && (
              <div className="flex justify-center py-2 border-t border-default mt-2 -mx-4 -mb-4">
                <button
                  type="button"
                  className="text-primary text-sm font-semibold hover:text-primary-dark uppercase tracking-wider"
                  onClick={() => setShowAllFuel((prev) => !prev)}
                >
                  {showAllFuel
                    ? translate("sidebar.buttons.seeLess")
                    : translate("sidebar.buttons.seeMore")}
                </button>
              </div>
            )}
          </div>
        )}
        {/* Cylinders Filter */}
        <div
          className="flex justify-between items-center cursor-pointer px-2 mb-2"
          onClick={() => toggleSection("cylinders")}
        >
          <span className="text-title text-[12px] font-semibold">
            {translate("sidebar.filters.cylinders")}
          </span>
          <span className="text-lg font-bold text-description">
            {expandedSections.cylinders ? "−" : "+"}
          </span>
        </div>
        {expandedSections.cylinders && (
          <div className="bg-card border mb-4 border-default rounded-xl text-description w-full overflow-hidden">
            <ul>
              {visibleCylinders.map((cylinder) => (
                <li
                  key={cylinder.id}
                  className="flex items-center justify-between border-b border-default"
                >
                  <label className="flex items-center cursor-pointer w-full px-4 py-2">
                    <input
                      type="checkbox"
                      checked={selectedCylinders.includes(cylinder.id)}
                      onChange={() => handleCylinderChange(cylinder.id)}
                      className="mr-2 custom-checkbox"
                    />
                    <span className="flex-1">{cylinder.name}</span>
                    {selectedCylinders.includes(cylinder.id) && (
                      <span className="text-sm text-description">
                        {dynamicCounts.cylinders[cylinder.id] || 0}
                      </span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
            {cylinders.length > 3 && (
              <div className="flex justify-center py-2">
                <button
                  type="button"
                  className="text-primary text-sm font-semibold hover:text-primary-dark uppercase tracking-wider"
                  onClick={() => setShowAllCylinders((prev) => !prev)}
                >
                  {showAllCylinders
                    ? translate("sidebar.buttons.seeLess")
                    : translate("sidebar.buttons.seeMore")}
                </button>
              </div>
            )}
          </div>
        )}
        {/* Engines Filter */}
        <div
          className="flex justify-between items-center cursor-pointer px-2 mb-2"
          onClick={() => toggleSection("engines")}
        >
          <span className="text-title text-[12px] font-semibold">
            {translate("sidebar.filters.engines")}
          </span>
          <span className="text-lg font-bold text-description">
            {expandedSections.engines ? "−" : "+"}
          </span>
        </div>
        {expandedSections.engines && (
          <div className="bg-card border mb-4 border-default rounded-xl text-description w-full overflow-hidden">
            <ul>
              {visibleEngines.map((engine) => (
                <li
                  key={engine.id}
                  className="flex items-center justify-between border-b border-default"
                >
                  <label className="flex items-center cursor-pointer w-full px-4 py-2">
                    <input
                      type="checkbox"
                      checked={selectedEngines.includes(engine.id)}
                      onChange={() => handleEngineChange(engine.id)}
                      className="mr-2 custom-checkbox"
                    />
                    <span className="flex-1">{engine.name}</span>
                    {selectedEngines.includes(engine.id) && (
                      <span className="text-sm text-description">
                        {dynamicCounts.engines[engine.id] || 0}
                      </span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
            {engines.length > 3 && (
              <div className="flex justify-center py-2">
                <button
                  type="button"
                  className="text-primary text-sm font-semibold hover:text-primary-dark uppercase tracking-wider"
                  onClick={() => setShowAllEngines((prev) => !prev)}
                >
                  {showAllEngines
                    ? translate("sidebar.buttons.seeLess")
                    : translate("sidebar.buttons.seeMore")}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Exterior Colors Filter */}
        <div
          className="flex justify-between items-center cursor-pointer px-2 mb-2"
          onClick={() => toggleSection("exteriorColors")}
        >
          <span className="text-title text-[12px] font-semibold">
            {translate("sidebar.filters.exteriorColors")}
          </span>
          <span className="text-lg font-bold text-description">
            {expandedSections.exteriorColors ? "−" : "+"}
          </span>
        </div>
        {expandedSections.exteriorColors && (
          <div className="bg-card border mb-4 border-default rounded-xl text-description w-full overflow-hidden">
            <ul>
              {visibleExteriorColors.map((color) => (
                <li
                  key={color.id}
                  className="flex items-center justify-between border-b border-default"
                >
                  <label className="flex items-center cursor-pointer w-full px-4 py-2">
                    <input
                      type="checkbox"
                      checked={selectedExteriorColors.includes(color.id)}
                      onChange={() => handleExteriorColorChange(color.id)}
                      className="mr-2 custom-checkbox"
                    />
                    <span className="flex-1">{color.name}</span>
                    {selectedExteriorColors.includes(color.id) && (
                      <span className="text-sm text-description">
                        {dynamicCounts.exteriorColors[color.id] || 0}
                      </span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
            {exteriorColors.length > 3 && (
              <div className="flex justify-center py-2">
                <button
                  type="button"
                  className="text-primary text-sm font-semibold hover:text-primary-dark uppercase tracking-wider"
                  onClick={() => setShowAllExteriorColors((prev) => !prev)}
                >
                  {showAllExteriorColors
                    ? translate("sidebar.buttons.seeLess")
                    : translate("sidebar.buttons.seeMore")}
                </button>
              </div>
            )}
          </div>
        )}
        {/* Interior Colors Filter */}
        <div
          className="flex justify-between items-center cursor-pointer px-2 mb-2"
          onClick={() => toggleSection("interiorColors")}
        >
          <span className="text-title text-[12px] font-semibold">
            {translate("sidebar.filters.interiorColors")}
          </span>
          <span className="text-lg font-bold text-description">
            {expandedSections.interiorColors ? "−" : "+"}
          </span>
        </div>
        {expandedSections.interiorColors && (
          <div className="bg-card border mb-4 border-default rounded-xl text-description w-full overflow-hidden">
            <ul>
              {visibleInteriorColors.map((color) => (
                <li
                  key={color.id}
                  className="flex items-center justify-between border-b border-default"
                >
                  <label className="flex items-center cursor-pointer w-full px-4 py-2">
                    <input
                      type="checkbox"
                      checked={selectedInteriorColors.includes(color.id)}
                      onChange={() => handleInteriorColorChange(color.id)}
                      className="mr-2 custom-checkbox"
                    />
                    <span className="flex-1">{color.name}</span>
                    {selectedInteriorColors.includes(color.id) && (
                      <span className="text-sm text-description">
                        {dynamicCounts.interiorColors[color.id] || 0}
                      </span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
            {interiorColors.length > 3 && (
              <div className="flex justify-center py-2">
                <button
                  type="button"
                  className="text-primary text-sm font-semibold hover:text-primary-dark uppercase tracking-wider"
                  onClick={() => setShowAllInteriorColors((prev) => !prev)}
                >
                  {showAllInteriorColors
                    ? translate("sidebar.buttons.seeLess")
                    : translate("sidebar.buttons.seeMore")}
                </button>
              </div>
            )}
          </div>
        )}
        {/* Primary Damage Types Filter */}
        <div
          className="flex justify-between items-center cursor-pointer px-2 mb-2"
          onClick={() => toggleSection("damageTypes")}
        >
          <span className="text-title text-[12px] font-semibold">
            {translate("sidebar.filters.damageTypes")}
          </span>
          <span className="text-lg font-bold text-description">
            {expandedSections.damageTypes ? "−" : "+"}
          </span>
        </div>
        {expandedSections.damageTypes && (
          <div className="bg-card border mb-4 border-default rounded-xl text-description w-full overflow-hidden">
            <ul>
              {visiblePrimaryDamageTypes.map((damageType) => (
                <li
                  key={damageType.id}
                  className="flex items-center justify-between border-b border-default"
                >
                  <label className="flex items-center cursor-pointer w-full px-4 py-2">
                    <input
                      type="checkbox"
                      checked={selectedPrimaryDamageTypes.includes(damageType.id)}
                      onChange={() =>
                        handlePrimaryDamageTypeChange(damageType.id)
                      }
                      className="mr-2 custom-checkbox"
                    />
                    <span className="flex-1">{damageType.name}</span>
                    {selectedPrimaryDamageTypes.includes(damageType.id) && (
                      <span className="text-sm text-description">
                        {dynamicCounts.primaryDamageTypes[damageType.id] || 0}
                      </span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
            {primaryDamageTypes.length > 3 && (
              <div className="flex justify-center py-2">
                <button
                  type="button"
                  className="text-primary text-sm font-semibold hover:text-primary-dark uppercase tracking-wider"
                  onClick={() => setShowAllPrimaryDamageTypes((prev) => !prev)}
                >
                  {showAllPrimaryDamageTypes
                    ? translate("sidebar.buttons.seeLess")
                    : translate("sidebar.buttons.seeMore")}
                </button>
              </div>
            )}
          </div>
        )}
        {/* Specifications Filter */}
        <div
          className="flex justify-between items-center cursor-pointer px-2 mb-2 mt-4"
          onClick={() => toggleSection("specifications")}
        >
          <span className="text-title text-[12px] font-semibold">
            {translate("sidebar.filters.specifications")}
          </span>
          <span className="text-lg font-bold text-description">
            {expandedSections.specifications ? "−" : "+"}
          </span>
        </div>
        {expandedSections.specifications && (
          <div className="mb-4 p-4 border border-default rounded-xl bg-card w-full">
            <div className="flex flex-wrap gap-2 w-full">
              {(showAllSpecifications
                ? specifications
                : specifications.slice(0, FEATURED_LIMIT)
              ).map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => handleSpecificationChange(type.id)}
                  className={`flex justify-between items-center px-3 py-1.5 rounded-full border text-sm transition cursor-pointer
                flex-auto w-auto
                ${selectedSpecifications.includes(type.id)
                      ? "border-primary-dark bg-primary text-description"
                      : "border-default text-description hover:border-primary-soft"
                    }`}
                >
                  <span title={type.name}>
                    {type.name}
                  </span>
                  {selectedSpecifications.includes(type.id) && (
                    <span className="text-description/70 text-xs ml-2">
                      {dynamicCounts.specifications[type.id] || 0}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {specifications.length > FEATURED_LIMIT && (
              <div className="flex justify-center py-2 border-t border-default mt-2 -mx-4 -mb-4">
                <button
                  type="button"
                  className="text-primary text-sm font-semibold hover:text-primary-dark uppercase tracking-wider"
                  onClick={() => setShowAllSpecifications((prev) => !prev)}
                >
                  {showAllSpecifications
                    ? translate("sidebar.buttons.seeLess")
                    : translate("sidebar.buttons.seeMore")}
                </button>
              </div>
            )}
          </div>
        )}
      </aside>
    );
  },
);
export default LeftSidebar;